/**
 * Whether the viewer can run sessions in ``slug`` at all: a usable
 * built-in agent login (``useProviderCredentials``, item 112's rule), or a
 * custom model of the organisation that can be picked now. A custom model
 * runs on its provider's key through the gateway, so a gateway user with
 * no personal login can still start sessions (the new-session form's
 * ``canCreate``).
 *
 * It builds on ``useProviderCredentials`` rather than restating it. The
 * job gates (brand composer, prompt-fragment composer) keep using
 * ``useProviderCredentials`` alone: the backend's ``require_ai_account``
 * doesn't accept custom models for jobs.
 */
import { useAgentProviders } from "@/api/hooks";
import { useAvailableLlmProviders } from "@/api/llmProviders";
import { CUSTOM_MODEL_CLIS, hasPickableCustomModel } from "@/features/sessions/customModels";

import { useProviderCredentials } from "./useProviderCredentials";

export type SessionAccess = {
  /** Every query behind the answer has settled. */
  ready: boolean;
  /** The viewer can start a session. */
  any: boolean;
  /** Through a personal login or central billing (not only a custom model). */
  builtin: boolean;
  /** The deployment registers no agent runtime: nobody can link anything. */
  noneRegistered: boolean;
};

export function useCanRunSessions(slug: string | null): SessionAccess {
  const credentials = useProviderCredentials();
  const providers = useAgentProviders();
  const custom = useAvailableLlmProviders(slug);

  const list = Array.isArray(providers.data) ? providers.data : null;
  const registered = (cli: string) => list === null || list.some((p) => p.slug === cli);
  const viaCustomModel = CUSTOM_MODEL_CLIS.some(
    (cli) => registered(cli) && hasPickableCustomModel(custom.data ?? [], cli),
  );

  return {
    ready: credentials.ready && !providers.isLoading && !custom.isLoading,
    any: credentials.any || viaCustomModel,
    builtin: credentials.any,
    noneRegistered: list !== null && list.length === 0,
  };
}
